import { inject, Injectable } from '@angular/core';
import { OTWebVitals } from '@lentau/core/open-telemetry/web-vitals/shared/ot-webvitals.interfaces';
import { setOpenTelemetryProvider } from './open-telemetry.facade';
import { OpenTelemetryConfigFactory } from './config/open-telemetry.config.factory';
import { OtelcolConfig } from './config/open-telemetry.interfaces';
import { PerformanceMetricsService } from './performance-metrics/performance-metrics.service';
import { PerformanceMetricsBufferService } from './performance-metrics/performance-metrics-buffer.service';
import { BrowserApiSupportService } from '@lentau/ui-kit/core/services/browser-api-support/browser-api-support.service';
import { environment } from '@lentau/core/environments';

export function isOpenTelemetryEnvEnabled(browserApi: BrowserApiSupportService): boolean {
  if (browserApi.isServer()) {
    return false;
  }

  if (browserApi.isBrowser() && browserApi.isSearchBot()) {
    return false;
  }

  if (environment.isEnableOpenTelemetry === false) {
    return false;
  }

  return true;
}

@Injectable({
  providedIn: 'root',
})
export class OpenTelemetryService {
  private openTelemetryConfigFactory: OpenTelemetryConfigFactory = inject(OpenTelemetryConfigFactory);
  private performanceMetricsService: PerformanceMetricsService = inject(PerformanceMetricsService);
  private bufferService: PerformanceMetricsBufferService = inject(PerformanceMetricsBufferService);
  private browserApi: BrowserApiSupportService = inject(BrowserApiSupportService);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public otelProvider: any = null;

  private otelConfig: OtelcolConfig = this.openTelemetryConfigFactory.get();

  private isInitialized: boolean = false;
  private initPromise: Promise<void> | null = null;

  public async ensureInitialized(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    if (!isOpenTelemetryEnvEnabled(this.browserApi)) {
      this.isInitialized = true;
      return;
    }

    if (this.initPromise !== null) {
      await this.initPromise;
      return;
    }

    this.initPromise = this.init();

    try {
      await this.initPromise;
    } finally {
      this.initPromise = null;
    }
  }

  private async init(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    if (!isOpenTelemetryEnvEnabled(this.browserApi)) {
      this.isInitialized = true;
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [
      { WebTracerProvider, BatchSpanProcessor, SimpleSpanProcessor },
      { OTLPTraceExporter },
      { Resource },
      { SEMRESATTRS_SERVICE_NAME },
      { registerInstrumentations },
      { XMLHttpRequestInstrumentation },
      { ParentBasedSampler, TraceIdRatioBasedSampler },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ]: any[] = await Promise.all([
      import('@opentelemetry/sdk-trace-web'),
      import('@opentelemetry/exporter-trace-otlp-http'),
      import('@opentelemetry/resources'),
      import('@opentelemetry/semantic-conventions'),
      import('@opentelemetry/instrumentation'),
      import('@opentelemetry/instrumentation-xml-http-request'),
      import('@opentelemetry/sdk-trace-base'),
    ]);

    const probabilitySampler: number = Number(this.otelConfig.commonConfig.probabilitySampler);
    const sanitizedProbabilitySampler: number =
      Number.isFinite(probabilitySampler) && probabilitySampler >= 0 && probabilitySampler <= 1
        ? probabilitySampler
        : 1;

    this.otelProvider = new WebTracerProvider({
      resource: new Resource({
        [SEMRESATTRS_SERVICE_NAME]: this.otelConfig.commonConfig.serviceName,
      }),
      sampler: new ParentBasedSampler({
        root: new TraceIdRatioBasedSampler(sanitizedProbabilitySampler),
      }),
    });

    if (this.otelConfig.commonConfig.console) {
      // Log traces to the console
      this.otelProvider.addSpanProcessor(
        new SimpleSpanProcessor(new (await import('@opentelemetry/sdk-trace-web')).ConsoleSpanExporter())
      );
    }

    // Batch traces before sending them
    this.otelProvider.addSpanProcessor(
      new BatchSpanProcessor(
        new OTLPTraceExporter({
          ...this.otelConfig.otelcolConfig,
        })
      )
    );

    registerInstrumentations({
      instrumentations: [new XMLHttpRequestInstrumentation()],
    });

    setOpenTelemetryProvider(this.otelProvider);

    // Инициализируем буфер после установки провайдера
    void this.bufferService.initialize();

    this.isInitialized = true;
  }

  public sendWebVitals(webVitals: OTWebVitals): void {
    void this.performanceMetricsService.trackWebVitals(webVitals);
  }
}
